import { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { 
  Rocket, 
  AlertTriangle, 
  Clock, 
  HelpCircle, 
  CheckCircle,
  ArrowRight,
  FileText,
  Ban,
  Loader2,
  MoreVertical,
  Settings2,
  ChevronRight
} from 'lucide-react'
import type { HealthCardResponse, StageJudgement, StageCompletion, DealStage, FatalGapInfo, RiskLevel } from '@/types'
import { clsx } from 'clsx'
import StageProgressBar, { StageOverrideDialog } from './StageProgressBar'

// TASK-H7: 风险分组配置
interface RiskGroupConfig {
  level: RiskLevel
  label: string
  icon: string
  color: string
  textColor: string
  bgColor: string
  borderColor: string
}

const RISK_GROUP_CONFIG: Record<string, RiskGroupConfig> = {
  fatal_gap: {
    level: 'fatal_gap',
    label: '致命缺口',
    icon: '⚠️',
    color: 'red',
    textColor: 'text-red-700',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
  },
  key_pending: {
    level: 'key_pending',
    label: '关键待验证',
    icon: '🔶',
    color: 'orange',
    textColor: 'text-orange-700',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
  },
}

interface RiskGroup extends RiskGroupConfig {
  gaps: FatalGapInfo[]
}

// TASK-H7: 按风险等级分组假设
const groupGapsByRisk = (gaps: FatalGapInfo[]): RiskGroup[] => {
  const groups: Record<string, FatalGapInfo[]> = {
    fatal_gap: [],
    key_pending: [],
  }

  gaps.forEach(gap => {
    // 优先使用 riskLevel，fallback 到 impactScore 判断
    const risk = gap.riskLevel || (gap.impactScore > 0.7 ? 'fatal_gap' : 'key_pending')
    if (risk === 'fatal_gap') {
      groups.fatal_gap.push(gap)
    } else if (risk === 'key_pending') {
      groups.key_pending.push(gap)
    }
  })

  // 按维度排序
  const sortByDimension = (a: FatalGapInfo, b: FatalGapInfo) => 
    (a.dimension || '').localeCompare(b.dimension || '')

  return (['fatal_gap', 'key_pending'] as const)
    .filter(level => groups[level].length > 0)
    .map(level => ({
      ...RISK_GROUP_CONFIG[level],
      gaps: groups[level].sort(sortByDimension),
    }))
}

interface HealthCardProps {
  data: HealthCardResponse
  compact?: boolean
  onClick?: () => void
  onMarkGapNotApplicable?: (gapId: string, reason?: string) => Promise<void>
  // T9: 阶段感知 props
  stageJudgement?: StageJudgement
  stageCompletion?: StageCompletion
  onStageOverride?: (stage: DealStage, reason?: string) => Promise<void>
  onClearStageOverride?: () => Promise<void>
  // 控制是否隐藏内置的阶段进度条（用于外部独立渲染）
  hideStageProgress?: boolean
  // TASK-H7: 假设点击跳转回调
  onHypothesisSelect?: (hypothesisId: string, dimension: string) => void
}

/**
 * 健康卡组件 - 首页体检卡展示
 * 
 * 支持5种状态：
 * - cold: 冷启动（无数据）
 * - red: 致命缺口（高风险）
 * - gray: 证据不足（低置信度）
 * - yellow: 推进阻滞
 * - green: 健康运行
 */
export default function HealthCard({ 
  data, 
  compact = false, 
  onClick, 
  onMarkGapNotApplicable,
  stageJudgement,
  stageCompletion,
  onStageOverride,
  hideStageProgress = false,
  onClearStageOverride,
  onHypothesisSelect,
}: HealthCardProps) {
  const { cardState, score, confidence, fatalGaps, evidenceCount, daysWithoutStrongSignal } = data
  
  // TASK-H7: 按风险等级分组
  const riskGroups = useMemo(() => groupGapsByRisk(fatalGaps), [fatalGaps])
  const [markingGapId, setMarkingGapId] = useState<string | null>(null)
  const [showGapMenu, setShowGapMenu] = useState<string | null>(null)
  // T9: 阶段覆盖弹窗状态
  const [showOverrideDialog, setShowOverrideDialog] = useState(false)

  // 图标映射
  const IconComponent = useMemo(() => {
    switch (cardState.stateType) {
      case 'cold':
        return Rocket
      case 'red':
        return AlertTriangle
      case 'yellow':
        return Clock
      case 'gray':
        return HelpCircle
      case 'green':
        return CheckCircle
      default:
        return HelpCircle
    }
  }, [cardState.stateType])

  // 卡片样式
  const cardStyles = useMemo(() => {
    const base = 'relative overflow-hidden rounded-2xl border transition-all duration-300'
    
    switch (cardState.stateType) {
      case 'cold':
        return clsx(base, 'bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200')
      case 'red':
        return clsx(base, 'bg-gradient-to-br from-red-50 to-red-100 border-red-200')
      case 'yellow':
        return clsx(base, 'bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200')
      case 'gray':
        return clsx(base, 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200')
      case 'green':
        return clsx(base, 'bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200')
      default:
        return clsx(base, 'bg-white border-gray-200')
    }
  }, [cardState.stateType])

  // 图标颜色
  const iconColor = useMemo(() => {
    switch (cardState.stateType) {
      case 'cold': return 'text-slate-400'
      case 'red': return 'text-red-500'
      case 'yellow': return 'text-amber-500'
      case 'gray': return 'text-gray-400'
      case 'green': return 'text-emerald-500'
      default: return 'text-gray-400'
    }
  }, [cardState.stateType])

  // 评分颜色
  const scoreColor = useMemo(() => {
    if (cardState.stateType === 'cold') return 'text-slate-400'
    if (score >= 70) return 'text-emerald-600'
    if (score >= 50) return 'text-amber-600'
    return 'text-red-600'
  }, [score, cardState.stateType])

  if (compact) {
    // 紧凑模式（列表卡片）
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={clsx(cardStyles, 'p-4 cursor-pointer hover:shadow-lg')}
        onClick={onClick}
      >
        <div className="flex items-center gap-4">
          {/* 图标 */}
          <div className={clsx('p-2 rounded-xl', cardState.stateType === 'cold' ? 'bg-slate-200' : `bg-${cardState.color}-100`)}>
            <IconComponent className={clsx('w-5 h-5', iconColor)} />
          </div>

          {/* 内容 */}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 truncate">{data.dealName}</h3>
            <p className="text-sm text-gray-500 truncate">{cardState.title}</p>
          </div>

          {/* 评分 */}
          <div className="text-right">
            <div className={clsx('text-2xl font-bold', scoreColor)}>
              {cardState.stateType === 'cold' ? '--' : score}
            </div>
            <div className="text-xs text-gray-400">
              {cardState.stateType === 'cold' ? '待启动' : `置信度 ${Math.round(confidence * 100)}%`}
            </div>
          </div>
        </div>
      </motion.div>
    )
  }

  // 处理阶段覆盖
  const handleStageOverride = async (stage: DealStage, reason?: string) => {
    if (onStageOverride) {
      await onStageOverride(stage, reason)
    }
    setShowOverrideDialog(false)
  }

  const handleClearOverride = async () => {
    if (onClearStageOverride) {
      await onClearStageOverride()
    }
    setShowOverrideDialog(false)
  }

  // 完整模式（详情页）
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      className={clsx(cardStyles, 'p-6')}
    >
      {/* 背景装饰 - pointer-events-none 防止遮挡点击 */}
      <div className="absolute top-0 right-0 w-32 h-32 opacity-10 pointer-events-none">
        <IconComponent className="w-full h-full" />
      </div>

      {/* T9: 阶段进度条 - BR-T9-19: 体检卡顶部显示完整6阶段进度条 */}
      {stageJudgement && !hideStageProgress && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-700">阶段进度</h3>
            {/* BR-T9-21: "手动调整阶段"按钮打开覆盖弹窗 */}
            {onStageOverride && (
              <button
                onClick={() => setShowOverrideDialog(true)}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
              >
                <Settings2 className="w-3.5 h-3.5" />
                手动调整阶段
              </button>
            )}
          </div>
          <StageProgressBar
            stageJudgement={stageJudgement}
            stageCompletion={stageCompletion}
            onOverrideClick={() => setShowOverrideDialog(true)}
          />
        </div>
      )}

      {/* 头部 */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={clsx(
            'p-3 rounded-xl',
            cardState.stateType === 'cold' ? 'bg-slate-200' : 
            cardState.stateType === 'red' ? 'bg-red-200' :
            cardState.stateType === 'yellow' ? 'bg-amber-200' :
            cardState.stateType === 'gray' ? 'bg-gray-200' :
            'bg-emerald-200'
          )}>
            <IconComponent className={clsx('w-6 h-6', iconColor)} />
          </div>
          <div>
            <span className={clsx(
              'inline-block px-2 py-0.5 rounded-full text-xs font-medium',
              cardState.tagColor || 'bg-gray-100 text-gray-600'
            )}>
              {cardState.tag}
            </span>
          </div>
        </div>

        {/* 评分仪表盘 */}
        <div className="text-right">
          <div className={clsx('text-4xl font-bold tracking-tight', scoreColor)}>
            {cardState.stateType === 'cold' ? '--' : score}
          </div>
          <div className="text-sm text-gray-500">
            {cardState.stateType === 'cold' ? '暂无评分' : (
              <>
                置信度 <span className="font-medium">{Math.round(confidence * 100)}%</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* 标题和描述 */}
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">
          {cardState.title}
        </h2>
        <p className="text-gray-600 text-sm leading-relaxed">
          {cardState.description}
        </p>
      </div>

      {/* 引用（如有） */}
      {cardState.quote && (
        <div className="mb-4 p-3 bg-white/50 rounded-lg border-l-2 border-gray-300">
          <p className="text-sm text-gray-500 italic">
            "{cardState.quote}"
          </p>
        </div>
      )}

      {/* TASK-H7: 风险假设分组展示 */}
      {cardState.stateType !== 'cold' && (
        <div className="mb-4">
          <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
            风险假设
          </h3>
          
          {riskGroups.length > 0 ? (
            <div className="space-y-3">
              {riskGroups.map((group) => (
                <div
                  key={group.level}
                  className={clsx(
                    'rounded-lg p-3 border',
                    group.bgColor,
                    group.borderColor
                  )}
                >
                  {/* 分组标题 */}
                  <h4 className={clsx(
                    'text-sm font-medium mb-2 flex items-center gap-2',
                    group.textColor
                  )}>
                    <span>{group.icon}</span>
                    {group.label}
                    <span className="text-xs font-normal opacity-75">
                      ({group.gaps.length})
                    </span>
                  </h4>
                  
                  {/* 假设列表 */}
                  <ul className="space-y-1.5">
                    {group.gaps.slice(0, 3).map((gap, index) => {
                      const gapId = gap.hypothesisId || `gap-${gap.dimension}-${index}`
                      const isMarking = markingGapId === gapId
                      const showMenu = showGapMenu === gapId

                      const handleMarkNotApplicable = async () => {
                        if (!onMarkGapNotApplicable) return
                        setMarkingGapId(gapId)
                        setShowGapMenu(null)
                        try {
                          await onMarkGapNotApplicable(gapId)
                        } finally {
                          setMarkingGapId(null)
                        }
                      }

                      const handleGapClick = () => {
                        if (gap.hypothesisId && onHypothesisSelect) {
                          onHypothesisSelect(gap.hypothesisId, gap.dimension)
                        }
                      }

                      return (
                        <li
                          key={gapId}
                          className={clsx(
                            'flex items-center gap-2 p-2 bg-white/60 rounded-lg group relative',
                            onHypothesisSelect && gap.hypothesisId && 'cursor-pointer hover:bg-white/80'
                          )}
                          onClick={handleGapClick}
                        >
                          <span className="text-xs text-gray-500 font-medium min-w-[40px]">
                            [{gap.dimension}]
                          </span>
                          <span className="text-sm text-gray-700 flex-1 truncate">
                            {gap.title}
                          </span>
                          
                          {/* 跳转箭头 */}
                          {onHypothesisSelect && gap.hypothesisId && (
                            <ChevronRight className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                          )}
                          
                          {/* 操作菜单按钮 */}
                          {onMarkGapNotApplicable && (
                            <div className="relative">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setShowGapMenu(showMenu ? null : gapId)
                                }}
                                className="p-1 opacity-0 group-hover:opacity-100 hover:bg-gray-200 rounded transition-all"
                              >
                                {isMarking ? (
                                  <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                                ) : (
                                  <MoreVertical className="w-4 h-4 text-gray-400" />
                                )}
                              </button>
                              
                              {/* 下拉菜单 */}
                              {showMenu && (
                                <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-10 min-w-[140px]">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleMarkNotApplicable()
                                    }}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                                  >
                                    <Ban className="w-4 h-4" />
                                    标记不适用
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </li>
                      )
                    })}
                    {group.gaps.length > 3 && (
                      <li className="text-xs text-gray-500 pl-2">
                        +{group.gaps.length - 3} 个其他假设
                      </li>
                    )}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-center">
              <CheckCircle className="w-5 h-5 text-green-500 mx-auto mb-1" />
              <p className="text-sm text-green-700">当前无高风险假设</p>
            </div>
          )}
        </div>
      )}

      {/* 底部统计 */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200/50">
        <div className="flex items-center gap-4 text-sm text-gray-500">
          <span className="flex items-center gap-1">
            <FileText className="w-4 h-4" />
            {evidenceCount} 条证据
          </span>
          {daysWithoutStrongSignal !== undefined && daysWithoutStrongSignal > 0 && (
            <span className="flex items-center gap-1 text-amber-600">
              <Clock className="w-4 h-4" />
              {daysWithoutStrongSignal}天无强信号
            </span>
          )}
        </div>

        {/* 行动按钮 */}
        <button className={clsx(
          'flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
          cardState.stateType === 'cold' ? 'bg-primary-600 text-white hover:bg-primary-700' :
          cardState.stateType === 'red' ? 'bg-red-600 text-white hover:bg-red-700' :
          'bg-gray-900 text-white hover:bg-gray-800'
        )}>
          {cardState.actionText}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* T9: 阶段覆盖弹窗 */}
      {stageJudgement && (
        <StageOverrideDialog
          isOpen={showOverrideDialog}
          onClose={() => setShowOverrideDialog(false)}
          currentStage={stageJudgement.stageFinal}
          onOverride={handleStageOverride}
          onClearOverride={stageJudgement.isOverridden ? handleClearOverride : undefined}
          isOverridden={stageJudgement.isOverridden}
        />
      )}
    </motion.div>
  )
}

/**
 * 健康卡骨架屏
 */
export function HealthCardSkeleton() {
  return (
    <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6 animate-pulse">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gray-200 rounded-xl" />
          <div className="w-16 h-5 bg-gray-200 rounded-full" />
        </div>
        <div className="text-right">
          <div className="w-12 h-10 bg-gray-200 rounded mb-1" />
          <div className="w-20 h-4 bg-gray-200 rounded" />
        </div>
      </div>
      <div className="space-y-2 mb-4">
        <div className="w-3/4 h-5 bg-gray-200 rounded" />
        <div className="w-full h-4 bg-gray-200 rounded" />
      </div>
      <div className="flex justify-between items-center pt-4 border-t border-gray-200">
        <div className="w-24 h-4 bg-gray-200 rounded" />
        <div className="w-28 h-8 bg-gray-200 rounded-lg" />
      </div>
    </div>
  )
}

